import {
  getOfficeBillingHistory,
  upsertInvoice,
  upsertPayment,
  type BillingHistoryDoc,
  type FirmInvoiceDoc,
} from "../Services/Billing";
import type { AccountingOfficeDetail } from "./accountingOfficeDetail";
import { BILLING_STATUS, isCancelledBillingDoc } from "./billingStatus";
import { paymentBelongsToInvoice } from "./billingLedgerUtils";
import {
  recordInvoiceBalanceReversal,
  resolveAppliedDebtFromInvoice,
} from "./accountingOfficeBalance";

export interface CancelAccountingInvoiceResult {
  cancelledPaymentCount: number;
}

export type CancelAccountingInvoiceActor = {
  performedById: string;
  performedByName: string;
};

function isAnnualInvoice(invoice: BillingHistoryDoc): boolean {
  return invoice.context?.kind === "membershipDues";
}

/**
 * Cancels an unsent invoice, cascades cancellation to linked payments, and
 * reverses any office balance adjustment from annual invoice creation.
 */
export async function cancelAccountingInvoice(
  invoice: BillingHistoryDoc,
  office: AccountingOfficeDetail,
  actor: CancelAccountingInvoiceActor = {
    performedById: "system",
    performedByName: "System",
  },
): Promise<CancelAccountingInvoiceResult> {
  if (invoice.type !== "invoice") {
    throw new Error("Only invoices can be cancelled.");
  }
  if (invoice.isSent === true) {
    throw new Error("Sent invoices cannot be cancelled.");
  }
  if (isCancelledBillingDoc(invoice)) {
    throw new Error("This invoice is already cancelled.");
  }

  const history = await getOfficeBillingHistory(office.id);
  const linkedPayments = history.filter(
    (doc) =>
      doc.type === "payment" &&
      !isCancelledBillingDoc(doc) &&
      paymentBelongsToInvoice(doc, invoice),
  );

  const cancelledInvoice: FirmInvoiceDoc = {
    ...(invoice as FirmInvoiceDoc),
    status: BILLING_STATUS.cancelled,
    balance: 0,
  };
  await upsertInvoice(cancelledInvoice);

  for (const payment of linkedPayments) {
    await upsertPayment({
      ...payment,
      status: BILLING_STATUS.cancelled,
    });
  }

  if (isAnnualInvoice(invoice)) {
    const appliedCredit = Number(invoice.creditApplied) || 0;
    const appliedDebt = resolveAppliedDebtFromInvoice(invoice);
    await recordInvoiceBalanceReversal({
      officeId: office.id,
      firmId: String(office.firm ?? "") || null,
      invoiceId: invoice.id,
      invoiceRef: invoice.invoiceNumber ?? invoice.id,
      appliedCredit,
      appliedDebt,
      performedById: actor.performedById,
      performedByName: actor.performedByName,
    });
  }

  return { cancelledPaymentCount: linkedPayments.length };
}

/**
 * Returns how many active payments would be cancelled with this invoice.
 */
export function countLinkedPaymentsForCancel(
  invoice: BillingHistoryDoc,
  history: BillingHistoryDoc[],
): number {
  return history.filter(
    (doc) =>
      doc.type === "payment" &&
      !isCancelledBillingDoc(doc) &&
      paymentBelongsToInvoice(doc, invoice),
  ).length;
}
